import React from "react";

import { format } from "date-fns";
import { es } from "date-fns/locale";

import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import StatusBadge from "~/pages/purchase-orders/components/sales-notes/StatusBadge";
import { SalesNotesSummaryProps } from "~/pages/purchase-orders/types/sales-notes";
import { formatCurrency } from "~/utils/helpers";
import { grossFromNet } from "~/utils/iva";
import { getDisplayId } from "~/utils/salesRequestDisplayUtils";

export const SalesNotesSummary: React.FC<SalesNotesSummaryProps> = ({
  displayedOrders,
  clientNames,
  getSellerInfo,
  handleOrderClick,
  isSeller,
  setViewMode,
  getEmptyStateMessage,
}) => {
  const goToTableView = () => {
    setViewMode("table");
  };

  return (
    <Card>
      <CardHeader className="pb-0">
        <CardTitle className="text-base">Notas de Venta Recientes</CardTitle>
      </CardHeader>
      <CardContent className="pt-4">
        <div className="overflow-x-auto">
          {displayedOrders.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              {getEmptyStateMessage()}
            </div>
          ) : (
            <Table className="border-b">
              <TableHeader>
                <TableRow>
                  <TableHead>ID</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Cliente</TableHead>
                  {!isSeller && <TableHead>Vendedor</TableHead>}
                  <TableHead>Monto (con IVA)</TableHead>
                  <TableHead>Fecha</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {displayedOrders.slice(0, 10).map((order, index) => (
                  <TableRow
                    key={order.id}
                    className="cursor-pointer duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both hover:bg-muted/50"
                    style={{ animationDelay: `${Math.min(index * 40, 400)}ms` }}
                    onClick={() => handleOrderClick(order)}
                  >
                    <TableCell className="font-medium">
                      {getDisplayId(order)}
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={order.status} />
                    </TableCell>
                    <TableCell>
                      {clientNames[order.clientId] || "Cliente sin nombre"}
                    </TableCell>
                    {!isSeller && (
                      <TableCell>
                        {getSellerInfo(order.createdBy).name}{" "}
                        {getSellerInfo(order.createdBy).lastname}
                      </TableCell>
                    )}
                    <TableCell>
                      {formatCurrency(
                        grossFromNet(order.totalAmount, order.isExento)
                      )}
                    </TableCell>
                    <TableCell>
                      {format(new Date(order.createdAt), "dd/MM/yyyy", {
                        locale: es,
                      })}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {displayedOrders.length > 10 && (
            <div className="mt-4 flex justify-center">
              <Button variant="outline" size="sm" onClick={goToTableView}>
                Ver todas las notas
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default SalesNotesSummary;
